import React, { useEffect, useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import arrowRightLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-right-large';
import repeatTokens from '@amzn/meridian-tokens/base/icon/repeat';
import { AssignmentAssociate, AssociateOverrideEntry, ProcessPath, SearchAssociateModalProps, Workstation } from '../models';
import { isValidStationAssignmentOverride } from '../api';
import { store } from '../../../../store';
import { showError } from '../../../../store/slices/errorSlice';
import AssociateNameRow from './AssociateNameRow';

/**
 * Find the workstation where the associate is currently assigned.
 */
const getAssociateWorkstation = (
  associate: AssignmentAssociate,
  processPaths: ProcessPath[],
): { workstation: Workstation; processPath: ProcessPath } | null => {
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      if (ws.assignedAssociates.some((a) => a.associateId === associate.associateId)) {
        return { workstation: ws, processPath: path };
      }
    }
  }
  return null;
};

const SearchAssociateModal: React.FC<SearchAssociateModalProps> = ({
  open,
  onClose,
  associates,
  processPaths,
  onSubmit,
  target,
  swapAssociates,
}) => {
  const [query, setQuery] = useState<string>('');
  const [selectedAssociate, setSelectedAssociate] = useState<AssignmentAssociate | null>(null);
  const [validating, setValidating] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [overrideChecked, setOverrideChecked] = useState<boolean>(false);

  const isSwapMode = !!swapAssociates;

  const filteredAssociates = useMemo(() => {
    if (isSwapMode) return [];
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    return associates.filter((a) => a.associateName.toLowerCase().includes(lower));
  }, [query, associates, isSwapMode]);

  // In assign mode, find source workstation name for selected associate
  const sourceWorkstationName = useMemo(() => {
    if (!selectedAssociate) return '';
    const result = getAssociateWorkstation(selectedAssociate, processPaths);
    return result?.workstation.workstationName ?? 'Unassigned';
  }, [selectedAssociate, processPaths]);

  // In swap mode, get workstation info for both associates
  const swapInfo = useMemo(() => {
    if (!swapAssociates) return null;
    const [a1, a2] = swapAssociates;
    const ws1 = getAssociateWorkstation(a1, processPaths);
    const ws2 = getAssociateWorkstation(a2, processPaths);
    return {
      associate1: a1,
      associate2: a2,
      workstation1Name: ws1?.workstation.workstationName ?? 'Unassigned',
      workstation2Name: ws2?.workstation.workstationName ?? 'Unassigned',
      workstation1: ws1,
      workstation2: ws2,
    };
  }, [swapAssociates, processPaths]);

  // Call isValidStationAssignmentOverride API
  useEffect(() => {
    // Assign mode: validate when an associate is selected
    if (!isSwapMode && selectedAssociate) {
      let cancelled = false;
      const validate = async () => {
        setValidating(true);
        setValidationErrors([]);
        setOverrideChecked(false);

        if (target.fclmProcessPathId === null) {
          store.dispatch(showError({
            title: 'Invalid Process Path',
            message: 'Process path ID is missing. Unable to validate override.',
          }));
          setValidating(false);
          return;
        }

        const overrides: AssociateOverrideEntry[] = [{
          associateId: selectedAssociate.associateId,
          fclmFunctionId: target.fclmFunctionId,
          fclmProcessPathId: target.fclmProcessPathId,
          targetWorkstationId: target.workstationId,
        }];

        const result = await isValidStationAssignmentOverride(overrides);
        if (!cancelled) {
          setValidationErrors(result.validationErrors);
          setValidating(false);
        }
      };
      validate();
      return () => { cancelled = true; };
    }

    // Swap mode: validate both associates immediately on open
    if (isSwapMode && swapInfo) {
      let cancelled = false;
      const validate = async () => {
        setValidating(true);
        setValidationErrors([]);
        setOverrideChecked(false);

        const { workstation1, workstation2 } = swapInfo;
        if (!workstation1 || !workstation2) {
          store.dispatch(showError({
            title: 'Invalid Swap',
            message: 'Both associates must be assigned to a workstation to swap.',
          }));
          setValidating(false);
          return;
        }

        if (workstation1.processPath.fclmProcessPathId == null || workstation2.processPath.fclmProcessPathId == null) {
          store.dispatch(showError({
            title: 'Invalid Process Path',
            message: 'Process path ID is missing. Unable to validate override.',
          }));
          setValidating(false);
          return;
        }

        const overrides: AssociateOverrideEntry[] = [
          {
            associateId: swapInfo.associate1.associateId,
            fclmFunctionId: workstation2.processPath.fclmFunctionId,
            fclmProcessPathId: workstation2.processPath.fclmProcessPathId,
            targetWorkstationId: workstation2.workstation.workstationId,
          },
          {
            associateId: swapInfo.associate2.associateId,
            fclmFunctionId: workstation1.processPath.fclmFunctionId,
            fclmProcessPathId: workstation1.processPath.fclmProcessPathId,
            targetWorkstationId: workstation1.workstation.workstationId,
          },
        ];

        const result = await isValidStationAssignmentOverride(overrides);
        if (!cancelled) {
          setValidationErrors(result.validationErrors);
          setValidating(false);
        }
      };
      validate();
      return () => { cancelled = true; };
    }
  }, [selectedAssociate, target, isSwapMode, swapInfo]);

  const handleDropdownSelect = (associate: AssignmentAssociate) => {
    setSelectedAssociate(associate);
    setQuery('');
  };

  const handleClose = () => {
    setQuery('');
    setSelectedAssociate(null);
    setValidationErrors([]);
    setOverrideChecked(false);
    onClose();
  };

  const handleSubmit = () => {
    if (isSwapMode && swapAssociates) {
      setValidationErrors([]);
      setOverrideChecked(false);
      onSubmit(swapAssociates[0].associateId);
    } else if (selectedAssociate) {
      const id = selectedAssociate.associateId;
      setQuery('');
      setSelectedAssociate(null);
      setValidationErrors([]);
      setOverrideChecked(false);
      onSubmit(id);
    }
  };

  const submitDisabled = validating || (validationErrors.length > 0 && !overrideChecked);
  const showConfirmation = isSwapMode || (selectedAssociate && !validating);
  const modalTitle = isSwapMode ? 'Swap associates' : 'Assign associate';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      scrollContainer="viewport"
      width="600px"
    >
      <Box spacingInset="300" data-testid="search-associate-modal">
        {/* Assign mode: search input */}
        {!isSwapMode && !selectedAssociate && (
          <>
            <Input
              value={query}
              onChange={setQuery}
              placeholder="Type associate name..."
              data-testid="search-associate-input"
            />
            {query.trim() && (
              <Box className="search-associate-modal__dropdown" spacingInset="200">
                {filteredAssociates.length > 0 ? (
                  filteredAssociates.map((associate) => (
                    <button
                      key={associate.associateId}
                      type="button"
                      className="search-associate-modal__item"
                      onClick={() => handleDropdownSelect(associate)}
                      data-testid={`search-associate-option-${associate.associateId}`}
                    >
                      <Text type="b300">{associate.associateName}</Text>
                    </button>
                  ))
                ) : (
                  <Text type="b200" color="secondary">
                    No matching associates found
                  </Text>
                )}
              </Box>
            )}
          </>
        )}

        {/* Loading state */}
        {validating && (
          <Box spacingInset="400">
            <Loader data-testid="search-associate-loader" />
          </Box>
        )}

        {/* Confirmation cards */}
        {showConfirmation && !validating && (
          <>
            <Box spacingInset="none none 400 none">
              <Text type="b300">
                {isSwapMode
                  ? 'Are you sure you want to swap these associates?'
                  : 'Are you sure you want to update this assignment?'}
              </Text>
            </Box>

            {/* Assign mode cards */}
            {!isSwapMode && selectedAssociate && (
              <Row alignmentVertical="center" spacing="300" widths={['fill', 'fit', 'fill']}>
                <Box type="outline" spacingInset="300">
                  <Text type="h300">{sourceWorkstationName}</Text>
                  <Box type="outline" width="100%">
                    <AssociateNameRow
                      associateName={selectedAssociate.associateName}
                      associateAlias={selectedAssociate.associateAlias}
                      showCheckbox={true}
                      checkboxChecked={true}
                      checkboxDisabled={true}
                      showCheckmark={false}
                      hasWarning={selectedAssociate.hasWarning}
                      data-testid={`search-associate-selected-${selectedAssociate.associateId}`}
                    />
                  </Box>
                </Box>
                <Icon tokens={arrowRightLargeTokens} />
                <Box type="outline" spacingInset="300">
                  <Text type="h300">{target.workstationName}</Text>
                  <Box type="outline" width="100%">
                    <AssociateNameRow
                      associateName={selectedAssociate.associateName}
                      associateAlias={selectedAssociate.associateAlias}
                      showCheckbox={true}
                      checkboxChecked={true}
                      checkboxDisabled={true}
                      showCheckmark={false}
                      hasWarning={selectedAssociate.hasWarning}
                      data-testid={`search-associate-target-${selectedAssociate.associateId}`}
                    />
                  </Box>
                </Box>
              </Row>
            )}

            {/* Swap mode cards */}
            {isSwapMode && swapInfo && (
              <Row alignmentVertical="center" spacing="300" widths={['fill', 'fit', 'fill']}>
                <Box type="outline" spacingInset="300">
                  <Text type="h300">{swapInfo.workstation1Name}</Text>
                  <Box type="outline" width="100%">
                    <AssociateNameRow
                      associateName={swapInfo.associate1.associateName}
                      associateAlias={swapInfo.associate1.associateAlias}
                      showCheckbox={true}
                      checkboxChecked={true}
                      checkboxDisabled={true}
                      showCheckmark={false}
                      hasWarning={swapInfo.associate1.hasWarning}
                      data-testid={`search-associate-swap-1-${swapInfo.associate1.associateId}`}
                    />
                  </Box>
                </Box>
                <Icon tokens={repeatTokens} />
                <Box type="outline" spacingInset="300">
                  <Text type="h300">{swapInfo.workstation2Name}</Text>
                  <Box type="outline" width="100%">
                    <AssociateNameRow
                      associateName={swapInfo.associate2.associateName}
                      associateAlias={swapInfo.associate2.associateAlias}
                      showCheckbox={true}
                      checkboxChecked={true}
                      checkboxDisabled={true}
                      showCheckmark={false}
                      hasWarning={swapInfo.associate2.hasWarning}
                      data-testid={`search-associate-swap-2-${swapInfo.associate2.associateId}`}
                    />
                  </Box>
                </Box>
              </Row>
            )}

            {validationErrors.length > 0 && (
              <Box spacingInset="400 none none none">
                <Text type="h100">Validation Errors</Text>
                {validationErrors.map((err, idx) => (
                  <Text key={idx} type="b200" color="error" data-testid={`validation-error-${idx}`}>
                    {err}
                  </Text>
                ))}
                <Box spacingInset="300 none none none">
                  <Checkbox
                    checked={overrideChecked}
                    onChange={setOverrideChecked}
                    data-testid="override-checkbox"
                  >
                    Override validation
                  </Checkbox>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      {showConfirmation && !validating && (
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              size="small"
              disabled={submitDisabled}
              onClick={handleSubmit}
              data-testid="search-associate-submit"
            >
              Submit
            </Button>
          </Row>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default SearchAssociateModal;
